/**
 * Small utilities. No external deps.
 */

const MAX_ATTR_LEN = 100;
const DANGEROUS_PATTERNS = [
  /<script/i,
  /javascript:/i,
  /data:/i,
  /vbscript:/i,
  /on\w+\s*=/i,
];

export function sanitizeSource(input: string): string {
  if (!input || typeof input !== 'string') return '';
  return input
    .trim()
    .slice(0, MAX_ATTR_LEN)
    .toLowerCase()
    .replace(/[^a-z0-9_\-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export function isSafeString(value: string): boolean {
  if (!value || typeof value !== 'string' || value.length > MAX_ATTR_LEN) return false;
  return !DANGEROUS_PATTERNS.some((p) => p.test(value));
}

export function isInternalDomain(url: string, internalDomains: string[]): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return internalDomains.some((d) => host === d || host.endsWith('.' + d));
  } catch {
    return false;
  }
}

export function buildUrl(baseUrl: string, params: Record<string, string | undefined>): string {
  const url = new URL(baseUrl);
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') url.searchParams.set(k, v);
  }
  return url.toString();
}

/** Extract the GA4 client ID from the _ga cookie. Returns '' if not present. */
export function readGA4ClientId(): string {
  const m = typeof document !== 'undefined'
    ? document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/)
    : null;
  if (!m) return '';
  // _ga value: "GA1.1.<random>.<timestamp>" — client ID is "<random>.<timestamp>"
  const parts = m[1].split('.');
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : '';
}

export function debounce<T extends (...args: never[]) => void>(fn: T, wait: number): T {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return ((...args: never[]) => {
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  }) as T;
}

export function safeJsonParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export function safeBase64Encode(value: string): string {
  // URL-safe base64 — `+` → `-`, `/` → `_`, no padding
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function safeBase64Decode(value: string): string | null {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '==='.slice((normalized.length + 3) % 4);
    return atob(padded);
  } catch {
    return null;
  }
}

let debugEnabled = false;
export function setDebug(on: boolean): void {
  debugEnabled = on;
}
export function log(...args: unknown[]): void {
  if (debugEnabled) console.log('[TDGAttribution]', ...args);
}
export function warn(...args: unknown[]): void {
  if (debugEnabled) console.warn('[TDGAttribution]', ...args);
}
